import { FIXED_DT } from '@mine-or-die/shared';
import { Time } from '../platform/time.js';

/**
 * Fixed timestep game loop with interpolation
 *
 * Runs physics/game logic at a fixed rate (60hz) while rendering as fast as possible.
 * The alpha value is used to interpolate between states for smooth rendering.
 */
export class GameLoop {
  private time: Time;
  private accumulator: number = 0;
  private running: boolean = false;
  private animationFrameId: number | null = null;

  // Callbacks
  private onFixedUpdate: ((dt: number) => void) | null = null;
  private onRender: ((alpha: number) => void) | null = null;
  private onFrameStart: (() => void) | null = null;

  // Stats
  private tickCount: number = 0;
  private maxAccumulator: number = FIXED_DT * 5; // Cap to prevent spiral of death

  constructor() {
    this.time = new Time();
  }

  /**
   * Set the fixed update callback (physics, game logic)
   * Called at FIXED_DT intervals (60hz by default)
   */
  setFixedUpdate(callback: (dt: number) => void): void {
    this.onFixedUpdate = callback;
  }

  /**
   * Set the render callback
   * Called every frame with interpolation alpha (0-1)
   */
  setRender(callback: (alpha: number) => void): void {
    this.onRender = callback;
  }

  /**
   * Set a callback to run at the start of each frame
   * Good for input polling
   */
  setFrameStart(callback: () => void): void {
    this.onFrameStart = callback;
  }

  /**
   * Start the game loop
   */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.time = new Time();
    this.accumulator = 0;
    this.tick();
  }

  /**
   * Stop the game loop
   */
  stop(): void {
    this.running = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Single frame execution (for debugging or step mode)
   */
  step(): void {
    if (this.onFixedUpdate) {
      this.onFixedUpdate(FIXED_DT);
    }
    this.tickCount++;
    if (this.onRender) {
      this.onRender(1);
    }
  }

  /**
   * Main loop tick
   */
  private tick = (): void => {
    if (!this.running) return;

    // Get delta time
    const dt = this.time.tick();

    // Call frame start callback (for input)
    if (this.onFrameStart) {
      this.onFrameStart();
    }

    // Add to accumulator (capped to prevent death spiral)
    this.accumulator += dt;
    if (this.accumulator > this.maxAccumulator) {
      this.accumulator = this.maxAccumulator;
    }

    // Run fixed updates
    while (this.accumulator >= FIXED_DT) {
      if (this.onFixedUpdate) {
        this.onFixedUpdate(FIXED_DT);
      }
      this.accumulator -= FIXED_DT;
      this.tickCount++;
    }

    // Calculate interpolation alpha
    const alpha = this.accumulator / FIXED_DT;

    // Render
    if (this.onRender) {
      this.onRender(alpha);
    }

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame(this.tick);
  };

  /**
   * Get the Time instance for timing info
   */
  getTime(): Time {
    return this.time;
  }

  /**
   * Get current FPS
   */
  getFps(): number {
    return this.time.getFps();
  }

  /**
   * Get total tick count
   */
  getTickCount(): number {
    return this.tickCount;
  }

  /**
   * Check if loop is running
   */
  isRunning(): boolean {
    return this.running;
  }

  /**
   * Get the fixed timestep value
   */
  getFixedDt(): number {
    return FIXED_DT;
  }
}
